import { useEffect, useState } from 'react';
import { getHandlingTimePerEtiket, type HandlingTimePerEtiketRij } from '../handlingTime.api';

/**
 * Vervolg behandeltijd-rapportage, CAP-4 — het per-etiket antwoord ophalen.
 *
 * Met de hand, zoals `useHandlingTimeNormen`, en niet via react-query: de toetsen van het
 * behandeltijdscherm renderen zonder `QueryClientProvider`, en een `useQuery` hier zou ze allemaal
 * laten omvallen op "No QueryClient set". Geen herpoging: een dichte poort of een verkeerde periode
 * wordt niet beter door de vraag te herhalen.
 */
export interface PerEtiketLezing {
  data: HandlingTimePerEtiketRij[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function useHandlingTimePerEtiket(
  period: { from?: string; to?: string },
  options: { enabled?: boolean; userId?: number; gln?: string } = {},
): PerEtiketLezing {
  const { from, to } = period;
  const { enabled = true, userId, gln } = options;
  const actief = enabled && Boolean(from) && Boolean(to);

  const [data, setData] = useState<HandlingTimePerEtiketRij[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(actief);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!actief) {
      setData(undefined);
      setError(null);
      setIsLoading(false);
      return;
    }
    // Een antwoord dat binnenkomt nadat de periode of het GLN al verwisseld is, mag de nieuwe stand
    // niet overschrijven: dan zou het oude etiketten onder een nieuwe periode zetten.
    let levend = true;
    setIsLoading(true);
    getHandlingTimePerEtiket({ from: from!, to: to! }, { userId, gln })
      .then((antwoord) => { if (levend) { setData(antwoord.data); setError(null); } })
      .catch((oorzaak: unknown) => {
        if (levend) setError(oorzaak instanceof Error ? oorzaak : new Error(String(oorzaak)));
      })
      .finally(() => { if (levend) setIsLoading(false); });
    return () => { levend = false; };
  }, [actief, from, to, userId, gln]);

  return { data, isLoading, isError: error !== null, error };
}
